import { Alert, Button, Checkbox, Input, Modal } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Icon from '../../Icon';
import Popover from '../../Popover';
import { isStateEqual } from '../../AgTable/utils';
import FavoriteViewItem from './FavoriteViewItem';
import Loading from '../../Loading';

const defaultFavoriteView = {
  current: null,
  viewList: [],
};

function getToSave(currentState) {
  // scroll 存下来无意义，同时如果保存了一滚动就会 isCurrentDirty true 了
  const { scrollLeft, ...other } = currentState;
  return other;
}

function FavoriteView(props) {
  const {
    currentState = {},
    canSaveGlobalSetting,
    onLoadFavoriteView,
    onUpdateFavoriteView,
    onSwitchFavoriteView,
  } = props;

  const { t } = useTranslation('icp-components');
  const [modal, contextHolder] = Modal.useModal();

  const [favoriteView, setFavoriteView] = useState(defaultFavoriteView);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [nameError, setNameError] = useState(null);

  const anchorEl = useRef(null);
  const nameRef = useRef(null);
  const globalRef = useRef(null);

  const { current, viewList } = favoriteView || defaultFavoriteView;

  useEffect(() => {
    if (!onLoadFavoriteView) {
      return;
    }
    onLoadFavoriteView().then((res) => {
      setFavoriteView(res || defaultFavoriteView);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (nameDialogOpen) {
        nameRef.current.focus();
      }
    }, 16);
  }, [nameDialogOpen]);

  const handleSaveNew = () => {
    // if (!settingKey) {
    //   modal.error({ content: t('toolbar.favorite-view.no-setting-key') });
    // } else {
    setNameDialogOpen(true);
    // }
  };

  const doUpdate = (...args) => {
    setLoading(true);
    onUpdateFavoriteView(...args).then((res) => {
      setFavoriteView(res);
      setLoading(false);
    });
  };

  const handleNameConfirm = (event) => {
    event.preventDefault();

    const name = nameRef.current.input.value;
    const isGlobal = globalRef.current.input.checked;

    if (!name) {
      return;
    }

    // check name duplicated
    if (viewList.find((view) => view.name === name)) {
      setNameError(t('toolbar.favorite-view.name-exist', { name }));
      return;
    }

    // string 表示 rename, 否则表示新增
    if (typeof nameDialogOpen === 'string') {
      const oldItem = viewList.find((item) => item.name === nameDialogOpen);
      const newItem = { ...oldItem, name };
      doUpdate('update', { oldItem, newItem });
    } else {
      const newItem = {
        name,
        isGlobal,
        content: getToSave(currentState),
      };
      doUpdate('add', { newItem });
    }

    setNameDialogOpen(false);
  };

  const handleDelete = (index) => {
    doUpdate('delete', { oldItem: viewList[index] });
    // 删除 current 过后切换回默认
    const isCurrentDeleted = viewList[index].name === current;
    if (isCurrentDeleted) {
      onSwitchFavoriteView();
      setFavoriteView((old) => ({ ...old, current: null }));
    }
  };

  const handleSwitchView = (view) => {
    doUpdate('switchCurrent', { name: view ? view.name : undefined });
    onSwitchFavoriteView(view ? view.content : undefined);
    // 先修改一下 state，然后等 doUpdate promise 完成过后再使用其返回值
    setFavoriteView((old) => ({ ...old, current: view ? view.name : null }));
    setOpen(false);
  };

  const handleSyncCurrent = (index) => {
    const oldItem = viewList[index];
    const newItem = { ...oldItem, content: getToSave(currentState) };
    doUpdate('update', { oldItem, newItem });
  };

  const savedCurrentState = useMemo(() => {
    return viewList.find((view) => view.name === current)?.content;
  }, [current, viewList]);

  const isCurrentDirty = useMemo(() => {
    return savedCurrentState && !isStateEqual(savedCurrentState, getToSave(currentState), true);
  }, [currentState, savedCurrentState]);

  const currentLabel = (() => {
    // TODO，default view 的 show dirty 暂时做不好，先不显示了，只给保存的 view 显示 dirty
    if (!current) {
      return <span>{t('toolbar.favorite-view.default')}</span>;
    }
    return (
      <span>
        {current}
        {isCurrentDirty ? <span> ({t('toolbar.favorite-view.current-is-dirty')})</span> : null}
      </span>
    );
  })();

  return (
    <>
      <Button
        className="icp-table-toolbar-favorite-view-button"
        type="text"
        icon={<Icon name="oct:bookmark" />}
        style={{ padding: '0 12px' }}
        onClick={() => setOpen(true)}
        ref={anchorEl}
      >
        {currentLabel}
        <Icon name="arrow-down" />
      </Button>
      <Popover
        className="icp-table-save-view icp-table-menu"
        placement="bottom-end"
        open={open}
        anchorEl={anchorEl.current}
        onClose={() => setOpen(false)}
      >
        <div
          className="item icp-clickable"
          onClick={handleSaveNew}
          style={{ justifyContent: 'flex-start' }}
        >
          <Icon name="oct:plus" />
          {t('toolbar.favorite-view.save-new')}
        </div>
        <hr />
        <div
          className={clsx('item icp-clickable', { selected: !current })}
          onClick={() => handleSwitchView()}
        >
          <span>{t('toolbar.favorite-view.default')}</span>
        </div>
        {viewList.map((view, index) => {
          return (
            <FavoriteViewItem
              key={view.name + index}
              modal={modal}
              view={view}
              currentLabel={currentLabel}
              isCurrent={current === view.name}
              isCurrentDirty={isCurrentDirty}
              onSwitch={handleSwitchView}
              onSync={() => handleSyncCurrent(index)}
              onRename={() => setNameDialogOpen(view.name)}
              onDelete={() => handleDelete(index)}
            />
          );
        })}
        {loading ? <Loading overlay={true} /> : null}
      </Popover>
      <Modal
        title={t('toolbar.favorite-view.name')}
        open={!!nameDialogOpen}
        destroyOnClose={true}
        onOk={handleNameConfirm}
        onCancel={() => setNameDialogOpen(false)}
      >
        <form onSubmit={handleNameConfirm}>
          <Input
            id="name"
            ref={nameRef}
            style={{ margin: '12px 0' }}
            defaultValue={typeof nameDialogOpen === 'string' ? nameDialogOpen : ''}
            onChange={() => {
              if (nameError) {
                setNameError(null);
              }
            }}
          />
          {canSaveGlobalSetting && typeof nameDialogOpen !== 'string' ? (
            <Checkbox id="isGlobal" ref={globalRef}>
              {t('toolbar.favorite-view.save-as-global')}
            </Checkbox>
          ) : null}
        </form>
        {nameError ? <Alert type="error" showIcon={true} message={nameError} /> : null}
      </Modal>
      {contextHolder}
    </>
  );
}

FavoriteView.propTypes = {
  currentState: PropTypes.shape({}),
  canSaveGlobalSetting: PropTypes.bool,
  onLoadFavoriteView: PropTypes.func,
  onUpdateFavoriteView: PropTypes.func,
  onSwitchFavoriteView: PropTypes.func,
};

export default FavoriteView;
